/**
 * One event bus, three sinks — TRACKING-SPEC.md.
 *   gtag (GA4, consent mode) · fbq (Meta Pixel, advanced matching after lead) ·
 *   Worker POST /event (batched, sendBeacon on unload → Supabase + Meta CAPI).
 */
import type { Settings } from './types.ts';
import { ATTRIBUTION_KEYS, captureAttribution, device, inAppBrowser, sessionId, uuid, sha256, leadId, metaCookies, once, firstTouch } from './session.ts';

type Params = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & { push?: unknown; callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string };
    _fbq?: unknown;
  }
}

/**
 * Meta events, one per funnel moment (TRACKING-SPEC.md): every step render is
 * a PageView, the first answer a QuizStart, the plan page an InitiateCheckout.
 * The browser and the Worker send the same event_id, so Meta keeps one.
 */
const META_STANDARD: Record<string, string> = {
  step_view: 'PageView',
  lead: 'CompleteRegistration',
  plan_view: 'InitiateCheckout',
  purchase: 'Purchase',
};
const META_CUSTOM: Record<string, string> = {
  quiz_start: 'QuizStart',
};
export const metaEventName = (name: string): string | undefined => META_STANDARD[name] ?? META_CUSTOM[name];

/** fire once per session: a reload or a step back must not repeat these */
const ONCE = new Set(['funnel_start', 'quiz_start', 'lead', 'plan_view', 'purchase']);

const GA4_ALIAS: Record<string, string> = {
  lead: 'generate_lead',
  plan_view: 'view_item_list',
  plan_select: 'select_item',
  checkout_start: 'begin_checkout',
};

export interface Common {
  funnel_code: string;
  funnel_version: number;
  lang: string;
  sid: string;
  lead_id?: string;
  step_id?: string;
  step_type?: string;
  step_index?: number;
  section?: string;
  mechanic?: string;
  archetype?: string;
  device: string;
  in_app_browser: boolean;
}

export interface TrackedEvent {
  name: string;
  event_id: string;
  ts: number;
  params: Params;
}

class Tracker {
  private settings: Settings | null = null;
  private common: Partial<Common> = {};
  private queue: TrackedEvent[] = [];
  private pending: TrackedEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /** failed sends so far; the delay doubles each time */
  private retries = 0;
  private consented = false;
  private pixelReady = false;
  private started = false;
  /** from the Worker's /geo: whether the visitor is in a consent country */
  private gdpr: boolean | null = null;
  /** false while "auto" consent is still waiting for geolocation */
  private decidedFlag = false;
  /**
   * false until the consent question is settled — granted, or declined, by
   * the visitor or by geolocation. The Worker batches wait for this, so the
   * server copy of the first PageView goes out with the right consent flag.
   */
  private released = false;
  private listeners = new Set<() => void>();

  init(settings: Settings, common: Partial<Common>): void {
    this.settings = settings;
    this.common = { ...this.common, ...common, sid: sessionId(), device: device(), in_app_browser: inAppBrowser() };
    if (this.started) return;
    this.started = true;

    // Consent defaults must be on the dataLayer before `config`, or the first
    // hits go out under the wrong storage state.
    this.bootGa4();
    if (settings.features.consentBanner === 'auto') {
      // nothing leaves the page until /geo has answered (or timed out): the
      // pixel must not fire a PageView for a UK visitor before the banner
      this.setConsent(false);
    } else {
      this.applyGeo(null);
    }

    addEventListener('pagehide', () => this.flush(true));
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush(true);
      // shown after a background load: whatever could not leave the page then, leaves now
      else if (this.released) this.flush();
    });
  }

  /**
   * "auto": the Worker's geolocation (Cloudflare's country header) decides —
   * UK/EU/EEA see the banner, everyone else does not. When geolocation is not
   * known the language is the fallback.
   */
  consentRequired(): boolean {
    const f = this.settings?.features.consentBanner;
    if (f === true) return true;
    if (f === false) return false;
    if (this.gdpr !== null) return this.gdpr;
    const lang = (this.common.lang ?? this.settings?.defaultLang ?? 'en').slice(0, 2);
    return (this.settings?.euLangs ?? []).includes(lang);
  }

  /**
   * The Worker's answer for this visitor (null when it could not be reached):
   * decides consent for "auto", releases the held events either way.
   */
  applyGeo(geo: { gdpr: boolean } | null): void {
    this.gdpr = geo?.gdpr ?? null;
    this.decidedFlag = true;
    if (this.consentRequired()) {
      // the banner decides; nothing goes anywhere until it is answered
      for (const fn of this.listeners) fn();
      return;
    }
    this.setConsent(true);
  }

  /** true once consent is decided (granted, denied, or waiting on the banner) */
  get decided(): boolean {
    return this.decidedFlag;
  }

  /** the banner re-renders when consent state changes */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get hasConsent(): boolean {
    return this.consented;
  }

  setContext(next: Partial<Common>): void {
    this.common = { ...this.common, ...next };
  }

  private bootGa4(): void {
    const id = this.settings?.analytics.ga4MeasurementId ?? '';
    if (!id || id.startsWith('PLACEHOLDER')) return;
    window.dataLayer = window.dataLayer || [];
    // the canonical snippet: gtag.js reads `arguments`, so push that, not an array
    if (!window.gtag) {
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer!.push(arguments);
      } as Window['gtag'];
    }
    // consent defaults first — before js/config — so the first hit obeys them
    this.pushConsent(!this.consentRequired(), 'default');
    window.gtag!('js', new Date());
    window.gtag!('config', id, { send_page_view: false, debug_mode: this.debug || undefined });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
    document.head.appendChild(s);
  }

  /** `?debug=1` turns on GA4 DebugView and Meta's test event code for this session. */
  private get debug(): boolean {
    try {
      return new URLSearchParams(location.search).get('debug') === '1';
    } catch {
      return false;
    }
  }

  private pushConsent(granted: boolean, mode: 'default' | 'update'): void {
    window.gtag?.('consent', mode, {
      ad_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }

  private bootPixel(): void {
    const id = this.settings?.analytics.metaPixelId ?? '';
    if (this.pixelReady || !id || id.startsWith('PLACEHOLDER')) return;
    if (!window.fbq) {
      const fbq = function (this: unknown, ...args: unknown[]) {
        const self = fbq as NonNullable<Window['fbq']>;
        const call = (self as { callMethod?: (...a: unknown[]) => void }).callMethod;
        if (call) call.apply(self, args);
        else self.queue!.push(args);
      } as NonNullable<Window['fbq']>;
      // fbevents.js drains `queue` and replaces `push`/`callMethod` on load
      fbq.push = fbq;
      fbq.queue = [];
      fbq.loaded = true;
      fbq.version = '2.0';
      window.fbq = fbq;
      window._fbq = fbq;
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(s);
    }
    // our step_view is the PageView; the pixel's automatic one on pushState
    // has no event id and would double-count every step
    (window.fbq as unknown as { disablePushState?: boolean }).disablePushState = true;
    window.fbq!('init', id);
    this.pixelReady = true;
  }

  /** Consent mode: denied until granted; Supabase events fire either way (first-party, no cookies). */
  setConsent(granted: boolean): void {
    const changed = this.consented !== granted;
    this.consented = granted;
    if (changed || granted) this.pushConsent(granted, 'update');
    for (const fn of this.listeners) fn();
    if (this.decidedFlag && !this.released) {
      // the question is settled either way: release the held batches
      this.released = true;
      this.flush();
    }
    if (!granted) return;
    this.bootPixel();
    const held = this.pending;
    this.pending = [];
    for (const e of held) this.toBrowserSinks(e);
  }

  private commonParams(): Params {
    const attribution = captureAttribution();
    const out: Params = { ...this.common, lead_id: leadId() ?? undefined };
    for (const k of ATTRIBUTION_KEYS) if (attribution[k]) out[k] = attribution[k];
    const { fbp, fbc } = metaCookies();
    if (fbp) out.fbp = fbp;
    if (fbc) out.fbc = fbc;
    const touch = firstTouch();
    if (touch.landing_url) out.landing_url = touch.landing_url;
    if (touch.angle) out.angle = touch.angle;
    return out;
  }

  /**
   * `extra.event_id` overrides the generated id — the purchase uses Paddle's
   * transaction id so the browser event and the webhook's are the same to Meta.
   */
  emit(name: string, extra: Params = {}): TrackedEvent {
    const { event_id: givenId, ...rest } = extra;
    const event: TrackedEvent = {
      name,
      event_id: typeof givenId === 'string' && givenId ? givenId : uuid(),
      ts: Date.now(),
      params: { ...this.commonParams(), ...rest },
    };
    if (ONCE.has(name) && !once(name)) {
      if (import.meta.env.DEV) console.debug('[track] skipped, already fired this session:', name);
      return event;
    }
    if (this.consented) this.toBrowserSinks(event);
    else this.pending.push(event);
    this.toWorker(event, !!metaEventName(name));
    if (import.meta.env.DEV) console.debug('[track]', name, event.params);
    return event;
  }

  private toBrowserSinks(event: TrackedEvent): void {
    const ga4Name = GA4_ALIAS[event.name] ?? event.name;
    window.gtag?.('event', ga4Name, {
      ...event.params,
      event_id: event.event_id,
      ...(this.debug ? { debug_mode: true } : {}),
    });
    const standard = META_STANDARD[event.name];
    const custom = META_CUSTOM[event.name];
    if (standard) window.fbq?.('track', standard, this.metaCustomData(event), { eventID: event.event_id });
    else if (custom) window.fbq?.('trackCustom', custom, this.metaCustomData(event), { eventID: event.event_id });
  }

  private metaCustomData(event: TrackedEvent): Params {
    const { value, currency, plan_id, archetype, funnel_code, lang, utm_content, preselected, angle } = event.params as Params;
    const out: Params = {};
    if (funnel_code !== undefined) out.funnel_code = funnel_code;
    if (angle !== undefined) out.angle = angle;
    if (lang !== undefined) out.lang = lang;
    if (utm_content !== undefined) out.utm_content = utm_content;
    if (value !== undefined) out.value = value;
    if (currency !== undefined) out.currency = currency;
    if (plan_id !== undefined) {
      out.content_name = plan_id;
      // the plan page: which plan was on screen; the purchase: which was bought
      if (event.name === 'purchase' || preselected) out.content_ids = [plan_id];
    }
    if (archetype !== undefined) out.content_category = archetype;
    return out;
  }

  /** Advanced Matching once the lead is known. */
  async identify(email: string): Promise<string> {
    const hash = await sha256(email);
    const id = this.settings?.analytics.metaPixelId ?? '';
    if (this.consented && id && !id.startsWith('PLACEHOLDER')) window.fbq?.('init', id, { em: hash });
    window.gtag?.('set', 'user_properties', { archetype: this.common.archetype, funnel_code: this.common.funnel_code });
    return hash;
  }

  private toWorker(event: TrackedEvent, now = false): void {
    this.queue.push(event);
    if (!this.released) return; // released once consent is settled
    if (now || this.queue.length >= 10) {
      // a Meta event goes to the Worker straight away: the server copy should
      // reach Meta within seconds of the browser copy for deduplication
      this.flush();
      return;
    }
    if (!this.flushTimer) this.flushTimer = setTimeout(() => this.flush(), 3000);
  }

  flush(unloading = false): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    if (!this.queue.length) return;
    const batch = this.queue;
    this.queue = [];
    const url = `${this.workerBase()}/event`;
    const body = JSON.stringify({ events: batch, consent: this.consented, url: location.href });
    if (unloading && typeof navigator.sendBeacon === 'function') {
      // text/plain keeps the beacon a "simple" request: cross-origin (Pages →
      // Worker) a JSON content-type would need a preflight, and a preflight
      // during unload is exactly when it will not complete.
      if (!navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) this.requeue(batch);
      return;
    }
    fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body, keepalive: true })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        this.retries = 0;
      })
      .catch(() => {
        // analytics must never break the funnel — but a batch is not dropped
        // either. The Instagram in-app browser loads the page before the tap
        // and lets nothing out until then, which lost the landing PageView of
        // every ad visit: the batch goes back to the front of the queue and is
        // sent again, later.
        this.requeue(batch);
      });
  }

  private requeue(batch: TrackedEvent[]): void {
    this.queue = [...batch, ...this.queue];
    if (this.retries >= 6) return; // the unload beacon gets the last try
    const delay = Math.min(30_000, 1000 * 2 ** this.retries);
    this.retries += 1;
    setTimeout(() => this.flush(), delay);
  }

  workerBase(): string {
    const configured = this.settings?.analytics.workerUrl ?? '';
    // In dev and in the Docker image the Worker is reachable through the /api proxy.
    if (!configured || configured.startsWith('PLACEHOLDER') || import.meta.env.DEV || location.hostname === 'localhost') return '/api';
    return configured.replace(/\/$/, '');
  }
}

export const track = new Tracker();
